import React, { useState, useEffect } from 'react';
import { Route, Switch, useRouteMatch, Redirect, useHistory } from 'react-router-dom';
import {
  Admin as AuthAdmin, Users, Roles, FillRemainingContainer, UserAddedToRoleEvent, UserRemovedFromRoleEvent,
  UserSavedEvent, EntitlementsChangedOnRoleEvent, useAuthConfig
} from '@samc/auth-authorization';
import { useToken } from '@samc/auth-authentication-okta';
import { DefaultPublisher } from '@samc/common';
import Tabs from "./Components/Tabs.react";
import Tab from "./Components/Tab.react";
import { setActiveNav, setTitle } from '@/nav';
import { UserFieldValidator } from './Validators/UserFieldValidator';
import { TraderDisplayValidator } from './Validators/TraderDisplayValidator';
import { AuthUserGrid, AuthRoleGrid } from '@samc/auth-authorization-ui-react';

const Admin = () => {
  const match = useRouteMatch();
  const history = useHistory();
  const authConfig = useAuthConfig();
  const token = useToken();
  const [top, setTop] = useState(undefined);
  const [verificationToken, setVerificationToken] = useState(undefined);

  const getVerificationToken = async () => {
    const tokenResponse = await fetch(`/antiforgery/requestToken`, { method: "GET" })

    return await tokenResponse.text();
  };

  useEffect(() => {
    const header = document.querySelector('header.main-header');
    setTop(`${header.clientHeight}px`);

    document.getElementById('authSection')?.classList?.add('flex', 'flex-grow', 'flex-col');

    const userRemoved = async (id) => {
      await fetch(`/adminApi/v1/userUpdated/${id}`, { method: "POST", headers: { "RequestVerificationToken": await getVerificationToken() } });
    }

    const userAddedOrEdited = async (id) => {
      const userDtails = await fetch(`/adminApi/v1/user/${id}`, { method: "GET", headers: { "RequestVerificationToken": await getVerificationToken() } });
      const user = await userDtails.json();
      if (user.isDisabled === false) {
        // Check if the user has a folder in the LS system
        await fetch(`/ls/CheckUserFolder?user=${user.username}`, { method: "POST", headers: { "RequestVerificationToken": await getVerificationToken() } });
      }
    }

    const roleChanged = async (id) => {
      await fetch(`/adminApi/v1/roleUpdated/${id}`, { method: "POST", headers: { "RequestVerificationToken": await getVerificationToken() } });
    }

    var roleAddedSubId = DefaultPublisher.subscribe(UserAddedToRoleEvent.eventName, async (evt) => { await userAddedOrEdited(evt.userId) });
    var roleRemovedSubId = DefaultPublisher.subscribe(UserRemovedFromRoleEvent.eventName, async (evt) => { await userRemoved(evt.userId) });
    var userSavedSubId = DefaultPublisher.subscribe(UserSavedEvent.eventName, async (evt) => { await userAddedOrEdited(evt.User.id) });
    var roleEntitlementsChangedSubId = DefaultPublisher.subscribe(EntitlementsChangedOnRoleEvent.eventName, async (evt) => { await roleChanged(evt.roleId) });

    return () => {
      DefaultPublisher.unsubscribe(UserAddedToRoleEvent.eventName, roleAddedSubId);
      DefaultPublisher.unsubscribe(UserRemovedFromRoleEvent.eventName, roleRemovedSubId);
      DefaultPublisher.unsubscribe(UserSavedEvent.eventName, userSavedSubId);
      DefaultPublisher.unsubscribe(EntitlementsChangedOnRoleEvent.eventName, roleEntitlementsChangedSubId);
    }
  });

  useEffect(() => {
    setActiveNav();
    switch (history.location.pathname) {
      case "/admin/roles":
        setTitle("Role Manager");
        break;
      case "/admin/users":
        setTitle("User Manager");
        break;
    }
  }, [match]);

  useEffect(async () => {
    const token = await getVerificationToken();
    setVerificationToken(token);
  }, []);
  // Note: styling is currently being set up by the Vue tabs.
  // See the applied CSS in Tabs.vue to learn more.

  const userDataFields = {
    fields: [
      {
        fieldName: 'traderId', label: 'Trader ID', visible: new TraderDisplayValidator('traderId'),
        dataValidation: new UserFieldValidator('traderId')
      },
      {
        fieldName: 'clientUserId1', label: 'Client User ID 1', visible: null,
        dataValidation: new UserFieldValidator('clientUserId1')
      },
      {
        fieldName: 'clientUserId2', label: 'Client User ID 2', visible: null,
        dataValidation: new UserFieldValidator('clientUserId2')
      },
    ],
    showSsoIdField: true
  };

  // Similarly, adding grid-wrapper and ag-grid to get the grid styling assigned in Grid.vue
  return (
    <div className="grid-wrapper">
      <div className="ag-grid">
        {verificationToken !== undefined &&
          <AuthAdmin editorStyling={{ top: top, zIndex: 1000 }}>
            <Tabs>
              <Tab name="Users" link={`${match.path}/users`} classes="ml-0 rounded-tl" />
              <Tab name="Roles" link={`${match.path}/roles`} classes="ml-[-1px] rounded-tr" />
            </Tabs>
            <Switch>
              <FillRemainingContainer querySelectorForParent="#authSection">
                <Route path={`${match.path}/users`}>
                  <AuthUserGrid apiRoot={authConfig.apiUrl + '/api/v2'} jwt={token} verificationToken={verificationToken} userDataFieldSettings={userDataFields} />
                </Route>
                <Route path={`${match.path}/roles`}>
                  <AuthRoleGrid apiRoot={authConfig.apiUrl + '/api/v2'} jwt={token} verificationToken={verificationToken} userDataFieldSettings={userDataFields} />
                </Route>
                <Route exact path={`${match.path}`}>
                  <Redirect to={`${match.path}/users`} />
                </Route>
              </FillRemainingContainer>
            </Switch>
          </AuthAdmin>}
      </div>
    </div>
  );
}

export default Admin;